import { useMemo, useState, useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight, Layers, Plus, X } from "lucide-react";
import { Tooltip } from "@/components/atoms/Tooltip";
import { clsx } from "clsx";
import type { DiaryEntrySummary } from "@/types/entities";
import { formatCalendarTime, isBeforeToday, isBeforeYesterday, isToday, isYesterday, toDateKey } from "@/utils/date-helpers";
import { diaryOccurrenceDateKeys, isDiaryEntryCancelled } from "./diaryEntryUtils";

export type DiaryCalendarViewType = "month" | "week" | "day";

interface Props {
  entries: DiaryEntrySummary[];
  view: DiaryCalendarViewType;
  onViewChange: (view: DiaryCalendarViewType) => void;
  currentDate: Date;
  onCurrentDateChange: (date: Date) => void;
  onEntryClick?: (entry: DiaryEntrySummary, occurrenceDate: Date) => void;
  onDateClick?: (date: Date) => void;
  /** When set, render occurrences only on this exact YYYY-MM-DD date. */
  filterDate?: string;
  /** Per-entry display title overrides (e.g. "Viewing: <address>" resolved on the fly), keyed by entry id. */
  entryTitleOverrides?: Record<string, string>;
}

const MAX_VISIBLE_MONTH = 4;
const MAX_VISIBLE_WEEK = 10;

function getEventStyles(entry: DiaryEntrySummary): string {
  const type = (entry.type || "").toUpperCase();
  const title = (entry.title || "").toUpperCase();

  if (isDiaryEntryCancelled(entry)) {
    return "bg-slate-100 text-slate-500 line-through border border-dashed border-slate-300";
  }

  const isHoliday = type === "HOLIDAY" || title.includes("HOLIDAY") || title.includes("LEAVE") || title.includes("OFF");
  const isViewing = type === "VIEWING" || title.includes("VIEWING");
  const isValuation = type === "VALUATION" || title.includes("VALUATION") || title.includes("APPRAISAL");
  const isInspection = type === "INSPECTION" || type === "MAINTENANCE_REQUEST" || title.includes("INSPECTION") || title.includes("SURVEY") || title.includes("REPORT");
  const isMeeting = type === "MEETING" || title.includes("MEETING") || title.includes("DISCUSSION");
  const isFollowUp = type === "FOLLOW_UP" || title.includes("FOLLOW UP") || title.includes("FOLLOW-UP");

  if (isHoliday) return "bg-rose-500 text-white font-bold";
  if (isViewing) return "bg-sky-500 text-white font-semibold";
  if (isValuation) return "bg-violet-500 text-white font-semibold";
  if (isInspection) return "bg-amber-500 text-white font-semibold";
  if (isMeeting) return "bg-indigo-500 text-white font-semibold";
  if (isFollowUp) return "bg-emerald-500 text-white font-semibold";

  return "bg-slate-500 text-white font-medium";
}

function EventChip({ entry, styles, onClick, displayTitle }: { entry: DiaryEntrySummary; styles: string; onClick: () => void; displayTitle?: string }) {
  return (
    <button
      type="button"
      title=""
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      className={clsx("diary-chip w-full text-left px-1.5 py-0.5 rounded-[4px] text-[11px] transition-all hover:brightness-110 flex items-center gap-1.5", styles)}
    >
      <span className="font-semibold truncate">{displayTitle || entry.title || "(No Title)"}</span>
      <span className="opacity-70 shrink-0 ml-auto">{formatCalendarTime(entry.startDate)}</span>
    </button>
  );
}

interface DayEventListProps {
  dayEntries: DiaryEntrySummary[];
  dateStr: string;
  date: Date;
  view: DiaryCalendarViewType;
  preferPopoverUp?: boolean;
  expandedDate: string | null;
  setExpandedDate: (date: string | null) => void;
  popoverRef: React.RefObject<HTMLDivElement>;
  onEntryClick?: (entry: DiaryEntrySummary, occurrenceDate: Date) => void;
  canCreate: boolean;
  entryTitleOverrides?: Record<string, string>;
}

function DayEventList({ dayEntries, dateStr, date, view, preferPopoverUp = false, expandedDate, setExpandedDate, popoverRef, onEntryClick, canCreate, entryTitleOverrides }: DayEventListProps) {
  const limit = view === "day" ? dayEntries.length : view === "week" ? MAX_VISIBLE_WEEK : MAX_VISIBLE_MONTH;
  const visible = dayEntries.slice(0, limit);
  const overflow = dayEntries.length - limit;
  const isExpanded = expandedDate === dateStr;

  return (
    <div className="space-y-0.5 flex-1 relative">
      {visible.map((entry) => (
        <EventChip key={entry.id} entry={entry} styles={getEventStyles(entry)} displayTitle={entryTitleOverrides?.[entry.id]} onClick={() => onEntryClick?.(entry, date)} />
      ))}
      {overflow > 0 && (
        <button
          type="button"
          title=""
          onClick={(e) => { e.stopPropagation(); setExpandedDate(isExpanded ? null : dateStr); }}
          className="diary-chip w-full text-left px-2 py-1 text-[11px] font-semibold text-bridge-accent hover:text-bridge-accent/80 hover:bg-bridge-accent/20 rounded-md transition-colors"
        >
          +{overflow} more
        </button>
      )}
      {isExpanded && overflow > 0 && (
        <div
          ref={popoverRef}
          title=""
          onClick={(e) => e.stopPropagation()}
          className={clsx(
            "absolute z-50 left-0 w-full min-w-[180px] bg-bridge-surface border border-bridge-border/60 rounded-xl shadow-2xl p-2 flex flex-col",
            preferPopoverUp ? "bottom-0" : "top-0",
          )}
          style={{ maxHeight: "320px" }}
        >
          <div className="flex items-center justify-between px-1 pb-1 border-b border-bridge-border/30 flex-shrink-0">
            <span className="text-[11px] font-bold text-bridge-fg">
              {date.toLocaleString("default", { weekday: "short", month: "short", day: "numeric" })}
            </span>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setExpandedDate(null); }}
              className="text-bridge-muted hover:text-bridge-fg transition-colors"
            >
              <X size={12} />
            </button>
          </div>
          <div className="overflow-y-auto flex-1 space-y-1.5 pt-1.5 pr-0.5">
            {dayEntries.map((entry) => (
              <EventChip key={entry.id} entry={entry} styles={getEventStyles(entry)} displayTitle={entryTitleOverrides?.[entry.id]} onClick={() => { setExpandedDate(null); onEntryClick?.(entry, date); }} />
            ))}
          </div>
        </div>
      )}
      {dayEntries.length === 0 && (
        <div className="h-full flex flex-col items-center justify-center text-bridge-subtle/40 py-10 pointer-events-none">
          <Layers size={32} strokeWidth={1} />
          <span className="text-[10px] mt-2 font-medium">No events</span>
          {canCreate && (
            <span className="text-[10px] mt-1 text-bridge-accent/70">Click to add</span>
          )}
        </div>
      )}
    </div>
  );
}

export function DiaryCalendar({ entries, view, onViewChange, currentDate, onCurrentDateChange, onEntryClick, onDateClick, filterDate, entryTitleOverrides }: Props) {
  const [expandedDate, setExpandedDate] = useState<string | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!expandedDate) return;
    const handler = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setExpandedDate(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [expandedDate]);

  const daysInRange = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const date = currentDate.getDate();

    if (view === "month") {
      const firstDay = new Date(year, month, 1).getDay();
      const lastDate = new Date(year, month + 1, 0).getDate();
      const days = [];
      for (let i = 0; i < firstDay; i++) days.push(null);
      for (let i = 1; i <= lastDate; i++) days.push(new Date(year, month, i));
      return days;
    }

    if (view === "week") {
      const startOfWeek = new Date(currentDate);
      startOfWeek.setDate(date - currentDate.getDay());
      const days = [];
      for (let i = 0; i < 7; i++) {
        const d = new Date(startOfWeek);
        d.setDate(startOfWeek.getDate() + i);
        days.push(d);
      }
      return days;
    }

    return [new Date(currentDate)];
  }, [currentDate, view]);

  const entriesByDate = useMemo(() => {
    const map: Record<string, DiaryEntrySummary[]> = {};
    const seenIds = new Set<string>();
    const realDays = daysInRange.filter((d): d is Date => d != null);
    if (realDays.length === 0) return map;
    const rangeStart = realDays[0];
    const rangeEnd = new Date(realDays[realDays.length - 1]);
    rangeEnd.setHours(23, 59, 59, 999);
    entries.forEach((entry) => {
      if (!entry.startDate) return;
      const id = entry.id?.trim();
      if (id) {
        if (seenIds.has(id)) return;
        seenIds.add(id);
      }
      // Expand recurring entries across their occurrence days within the visible range.
      for (const dateKey of diaryOccurrenceDateKeys(entry, rangeStart, rangeEnd)) {
        if (!dateKey) continue;
        if (filterDate && dateKey !== filterDate) continue;
        if (!map[dateKey]) map[dateKey] = [];
        map[dateKey].push(entry);
      }
    });
    return map;
  }, [entries, daysInRange, filterDate]);

  const navigate = (direction: number) => {
    if (view === "month") {
      onCurrentDateChange(
        new Date(currentDate.getFullYear(), currentDate.getMonth() + direction, 1),
      );
      return;
    }

    const next = new Date(currentDate);
    if (view === "week") next.setDate(currentDate.getDate() + direction * 7);
    else next.setDate(currentDate.getDate() + direction);
    onCurrentDateChange(next);
  };

  const today = () => onCurrentDateChange(new Date());

  const title = useMemo(() => {
    if (view === "month") return currentDate.toLocaleString("default", { month: "long", year: "numeric" });
    if (view === "week") {
      const start = daysInRange[0] as Date;
      const end = daysInRange[6] as Date;
      if (start.getMonth() === end.getMonth()) {
        return `${start.toLocaleString("default", { month: "long" })} ${start.getDate()} - ${end.getDate()}, ${start.getFullYear()}`;
      }
      return `${start.toLocaleString("default", { month: "short" })} ${start.getDate()} - ${end.toLocaleString("default", { month: "short" })} ${end.getDate()}, ${start.getFullYear()}`;
    }
    return currentDate.toLocaleString("default", { day: "numeric", month: "long", year: "numeric" });
  }, [currentDate, view, daysInRange]);

  return (
    <div className="flex flex-col h-full bg-bridge-surface/30">
      <div className="flex items-center justify-between px-6 py-4 border-b border-bridge-border/40 bg-bridge-surface/50 backdrop-blur-sm">
        <div className="flex items-center gap-6">
          <h2 className="text-xl font-semibold text-bridge-fg min-w-[180px]">
            {title}
          </h2>
          <div className="flex items-center bg-bridge-surface/60 rounded-lg border border-bridge-border/60 p-0.5">
            <Tooltip content="Previous" side="bottom">
              <button onClick={() => navigate(-1)} className="p-1.5 hover:bg-bridge-surface rounded-md text-bridge-muted hover:text-bridge-fg transition-colors">
                <ChevronLeft size={18} />
              </button>
            </Tooltip>
            <button onClick={today} className="px-3 py-1 text-xs font-medium hover:bg-bridge-surface rounded-md text-bridge-muted hover:text-bridge-fg transition-colors">
              Today
            </button>
            <Tooltip content="Next" side="bottom">
              <button onClick={() => navigate(1)} className="p-1.5 hover:bg-bridge-surface rounded-md text-bridge-muted hover:text-bridge-fg transition-colors">
                <ChevronRight size={18} />
              </button>
            </Tooltip>
          </div>
        </div>

        <div className="flex items-center bg-bridge-surface/60 rounded-lg border border-bridge-border/60 p-1 gap-1">
          {(["month", "week", "day"] as const).map((v) => (
            <button
              key={v}
              onClick={() => onViewChange(v)}
              className={clsx(
                "px-4 py-1.5 text-xs font-bold uppercase tracking-wider rounded-md transition-all",
                view === v ? "bg-bridge-accent text-white shadow-md" : "text-bridge-muted hover:text-bridge-fg hover:bg-bridge-surface"
              )}
            >
              {v}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6">
        <div className={clsx(
          "grid gap-px bg-bridge-border/30 rounded-xl overflow-visible border border-bridge-border/40 shadow-2xl",
          view === "month" || view === "week" ? "grid-cols-7" : "grid-cols-1"
        )}>
          {(view === "month" || view === "week") && ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
            <div key={day} className="bg-bridge-surface/80 py-3 text-center text-[11px] font-bold uppercase tracking-widest text-bridge-subtle border-b border-bridge-border/20">
              {day}
            </div>
          ))}

          {daysInRange.map((date, i) => {
            const dateStr = date ? toDateKey(date) : "";
            const dayEntries = (dateStr ? entriesByDate[dateStr] : []) ?? [];
            const dayIsToday = date ? isToday(date) : false;
            const dayIsYesterday = date ? isYesterday(date) : false;
            const dayIsPast = date ? isBeforeToday(date) : false;
            const dayIsBlockedPast = date ? isBeforeYesterday(date) : false;
            const canCreate = Boolean(date && onDateClick && !dayIsBlockedPast);
            const canQuickAdd = Boolean(date && onDateClick && !dayIsPast);
            const totalRows =
              view === "month" || view === "week"
                ? Math.ceil(daysInRange.length / 7)
                : 1;
            const currentRow =
              view === "month" || view === "week"
                ? Math.floor(i / 7)
                : 0;
            const preferPopoverUp = totalRows > 1 && currentRow >= totalRows - 2;

            return (
              <div
                key={i}
                role={canCreate ? "button" : undefined}
                tabIndex={canCreate ? 0 : undefined}
                onClick={() => canCreate && onDateClick!(date!)}
                onKeyDown={(e) => {
                  if (!canCreate) return;
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onDateClick!(date!);
                  }
                }}
                title={
                  dayIsBlockedPast
                    ? "Events cannot be added on this day"
                    : canCreate
                      ? dayIsYesterday
                        ? "Click to add event on yesterday"
                        : "Click to add event"
                      : undefined
                }
                className={clsx(
                  "group/day bg-bridge-surface/40 p-2 transition-colors flex flex-col outline-none focus:outline-none focus-visible:outline-none",
                  view === "month" ? "h-[180px]" : view === "week" ? "min-h-[400px]" : "min-h-[500px]",
                  !date && "bg-transparent/5",
                  dayIsBlockedPast && onDateClick && "cursor-not-allowed",
                  canCreate && "cursor-pointer",
                  canCreate && !dayIsPast && "[&:hover:not(:has(.diary-chip:hover))]:bg-sky-50 [&:hover:not(:has(.diary-chip:hover))]:shadow-md [&:hover:not(:has(.diary-chip:hover))]:z-10 [&:hover:not(:has(.diary-chip:hover))]:relative",
                  dayIsYesterday && canCreate && "[&:hover:not(:has(.diary-chip:hover))]:bg-bridge-accent/5",
                )}
              >
                {date && (
                  <>
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex flex-col">
                        <span className={clsx(
                          "text-sm font-medium w-8 h-8 flex items-center justify-center rounded-full transition-colors",
                          dayIsToday && "bg-bridge-accent text-white shadow-lg shadow-bridge-accent/30",
                          dayIsBlockedPast && "text-bridge-muted/50",
                          !dayIsToday && dayIsPast && !dayIsBlockedPast && "text-bridge-muted/80",
                          !dayIsToday && !dayIsPast && "text-bridge-muted",
                        )}>
                          {date.getDate()}
                        </span>
                        {view === "day" && (
                          <span className="text-xs text-bridge-muted mt-1 font-semibold uppercase">
                            {date.toLocaleString("default", { weekday: "long" })}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        {dayEntries.length > 0 && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-bridge-accent/10 text-bridge-accent font-bold border border-bridge-accent/20">
                            {dayEntries.length} Events
                          </span>
                        )}
                        {canQuickAdd && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onDateClick?.(date);
                            }}
                            className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-bridge-accent text-white shadow-sm group-hover/day:brightness-110 group-has-[.diary-chip:hover]/day:brightness-100 transition-colors focus-ring"
                            aria-label={`Add event on ${date.toDateString()}`}
                            title="Add event"
                          >
                            <Plus
                              size={12}
                              strokeWidth={3}
                              className="transition-transform duration-300 group-hover/day:scale-125 group-has-[.diary-chip:hover]/day:scale-100"
                            />
                          </button>
                        )}
                      </div>
                    </div>
                    <DayEventList
                      dayEntries={dayEntries}
                      dateStr={dateStr}
                      date={date}
                      view={view}
                      preferPopoverUp={preferPopoverUp}
                      expandedDate={expandedDate}
                      setExpandedDate={setExpandedDate}
                      popoverRef={popoverRef}
                      onEntryClick={onEntryClick}
                      canCreate={canCreate}
                      entryTitleOverrides={entryTitleOverrides}
                    />
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
